import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { watchForSWUpdate, activatePendingSW } from "@/lib/push";
import { listPending } from "@/lib/offlineQueue";

const UnreadContext = createContext({
  total: 0, byService: {}, serviceNos: {}, generalUnread: 0, notifUnread: 0,
  prefs: { sound: true, vibration: true, preview: true },
  refresh: () => {}, refreshPrefs: () => {}, setPrefs: () => {},
});

// Web Audio ile kısa "ding" sesi — harici dosya bağımlılığı yok.
function playDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [
      { freq: 988, start: 0,    dur: 0.12 },
      { freq: 1319, start: 0.10, dur: 0.18 },
    ];
    tones.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.35, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
    setTimeout(() => ctx.close && ctx.close(), 1000);
  } catch { /* audio bloklanabilir — sessizce yok say */ }
}

function tryVibrate(pattern) {
  try {
    if (navigator && typeof navigator.vibrate === "function") {
      navigator.vibrate(pattern);
    }
    // Not: bazı platformlar sessizce yok sayar. Başarılı olduğunu iddia etmiyoruz.
  } catch { /* noop */ }
}

const DEFAULT_PREFS = { sound: true, vibration: true, preview: true };

export function UnreadProvider({ children }) {
  const { user } = useAuth();
  const [total, setTotal] = useState(0);
  const [byService, setByService] = useState({});
  const [serviceNos, setServiceNos] = useState({});
  const [generalUnread, setGeneralUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [prefs, setPrefsState] = useState(DEFAULT_PREFS);

  const prevByServiceRef = useRef(null); // null = henüz ilk fetch yapılmadı
  const prevGeneralRef = useRef(null);
  const lastDingAtRef = useRef(0);        // dedup: son ding zamanı
  const seenPushMsgIdsRef = useRef(new Set()); // SW'den gelen message_id'ler
  const prefsRef = useRef(DEFAULT_PREFS);   // sync erişim için

  const setPrefs = useCallback((next) => {
    setPrefsState((cur) => {
      const merged = { ...cur, ...next };
      prefsRef.current = merged;
      return merged;
    });
  }, []);

  const refreshPrefs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/preferences");
      const merged = { ...DEFAULT_PREFS, ...data };
      prefsRef.current = merged;
      setPrefsState(merged);
    } catch { /* ignore */ }
  }, []);

  const notifyIncoming = useCallback((serviceNo, delta, sid) => {
    const now = Date.now();
    // Dedup: 2 saniye içinde iki kere ding yok
    const withinWindow = now - lastDingAtRef.current < 2000;
    if (!withinWindow) {
      lastDingAtRef.current = now;
      if (prefsRef.current.sound) playDing();
      if (prefsRef.current.vibration) tryVibrate([200, 100, 200]);
    }
    toast.info(
      `${serviceNo}: ${delta} yeni mesaj`,
      {
        duration: 6000,
        action: sid ? {
          label: "Aç",
          onClick: () => { window.location.href = `/servis-kayitlari/${sid}`; },
        } : undefined,
      },
    );
  }, []);

  const refresh = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const [uc, gu, nu] = await Promise.all([
        api.get("/service-records/unread-counts"),
        api.get("/general-messages/unread-count").catch(() => ({ data: { count: 0 } })),
        api.get("/notifications/unread-count").catch(() => ({ data: { count: 0 } })),
      ]);
      const data = uc.data || {};
      const newByService = data?.by_service || {};
      const newServiceNos = data?.service_nos || {};
      const newGeneral = gu.data?.count || 0;
      const prev = prevByServiceRef.current;
      const prevGeneral = prevGeneralRef.current;

      // İlk fetch (sayfa yenilendi/login yapıldı) → bildirim YOK
      if (prev !== null) {
        const arrivedServices = [];
        Object.entries(newByService).forEach(([sid, cnt]) => {
          const prevCnt = prev[sid] || 0;
          if (cnt > prevCnt) {
            arrivedServices.push({
              sid,
              delta: cnt - prevCnt,
              serviceNo: newServiceNos[sid] || sid.slice(0, 8),
            });
          }
        });
        arrivedServices.forEach((s) => notifyIncoming(s.serviceNo, s.delta, s.sid));
        // Genel grup için de arttıysa toast
        if (prevGeneral !== null && newGeneral > prevGeneral) {
          notifyIncoming("Genel Servis Grubu", newGeneral - prevGeneral, null);
        }
      }

      prevByServiceRef.current = newByService;
      prevGeneralRef.current = newGeneral;
      setTotal(data?.total || 0);
      setByService(newByService);
      setServiceNos(newServiceNos);
      setGeneralUnread(newGeneral);
      setNotifUnread(nu.data?.count || 0);
    } catch { /* network blip — keep prior state */ }
  }, [user, notifyIncoming]);

  // Prefs sync
  useEffect(() => {
    if (user && user !== false) refreshPrefs();
  }, [user, refreshPrefs]);

  // SW postMessage dinle — push gelince anında refresh ve dedup
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (event) => {
      const payload = event.data;
      if (!payload || payload.type !== "era-push") return;
      const d = payload.data || {};
      if (d.message_id) {
        seenPushMsgIdsRef.current.add(d.message_id);
      }
      lastDingAtRef.current = Date.now();
      refresh();
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [refresh]);

  // Tur 3 — Yeni SW sürümü hazır olduğunda kontrollü aktifleştirme banner'ı
  useEffect(() => {
    if (!user || user === false) return;
    let shown = false;
    return watchForSWUpdate(async (_reg) => {
      if (shown) return;
      shown = true;
      // Offline kuyruk kontrolü — kullanıcı kuyruğunda mesaj varsa uyarı ver, otomatik geçme
      let pendingCount = 0;
      try {
        if (user?.id) pendingCount = (await listPending(user.id)).length;
      } catch { pendingCount = 0; }
      const desc = pendingCount > 0
        ? `Kuyrukta ${pendingCount} bekleyen mesajınız var. Şimdi güncellerseniz sayfa yenilenir; mesajlar korunur ancak yeniden denenir.`
        : "Uygulamanın yeni sürümü hazır. Yenilemek ister misiniz?";
      toast.message("Yeni sürüm hazır", {
        description: desc,
        duration: Infinity,
        action: {
          label: "Şimdi yenile",
          onClick: () => { activatePendingSW(); },
        },
      });
    });
  }, [user]);

  useEffect(() => {
    if (!user || user === false) {
      setTotal(0);
      setByService({});
      setServiceNos({});
      setGeneralUnread(0);
      setNotifUnread(0);
      prevByServiceRef.current = null;
      prevGeneralRef.current = null;
      seenPushMsgIdsRef.current = new Set();
      return;
    }
    let intervalId = null;
    const start = () => {
      if (intervalId) return;
      refresh();
      intervalId = setInterval(refresh, 10000);
    };
    const stop = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") { start(); }
      else { stop(); }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, refresh]);

  return (
    <UnreadContext.Provider value={{
      total, byService, serviceNos, generalUnread, notifUnread,
      prefs, refresh, refreshPrefs, setPrefs,
    }}>
      {children}
    </UnreadContext.Provider>
  );
}

export const useUnread = () => useContext(UnreadContext);
